import React, {useMemo, useRef, useState, useEffect} from 'react'
import {
    useScroll,
    useInView,
    useReducedMotion,
    useMotionValue,
    useMotionValueEvent,
    animate
} from 'framer-motion'
import {Box} from '@chakra-ui/react3'
import PropTypes from 'prop-types'

// Maps normalized progress (0..1) to character index
const idxFromProgress = (p, total) => {
    if (total <= 0) return 0
    const step = 1 / total
    return Math.max(0, Math.min(total, Math.floor(p / step)))
}

const TypewrighterAnimation = ({
    children,
    from,
    to,
    duration = 1.5, // total time (sec) for full transition
    offset = ['start 95%', 'end 5%'], // defines when animation starts/ends in viewport
    ...props
}) => {
    const reduce = useReducedMotion()
    const text = typeof children === 'string' ? children : String(children ?? '')
    const chars = useMemo(() => Array.from(text), [text])

    const ref = useRef(null)
    const {scrollYProgress} = useScroll({target: ref, offset})
    const inView = useInView(ref, {amount: 0.1}) // small hysteresis to detect visibility

    const progressMotionValue = useMotionValue(0) // animated progress value (0..1)
    const animationRef = useRef(null)
    const prevProgRef = useRef(null) // stores previous scrollYProgress for direction detection
    const [coloredCount, setColoredCount] = useState(0)

    // Update colored character count whenever progress changes
    useMotionValueEvent(progressMotionValue, 'change', (currentProgress) => {
        const nextColoredIndex = idxFromProgress(currentProgress, chars.length)
        setColoredCount((previous) => (previous !== nextColoredIndex ? nextColoredIndex : previous))
    })

    // React to scroll changes
    useMotionValueEvent(scrollYProgress, 'change', (scrollProgress) => {
        if (!inView) {
            // Pause when element is outside viewport
            animationRef.current?.stop?.()
            return
        }

        if (reduce) {
            // Accessibility: instantly jump to end/start when reduced motion is preferred
            progressMotionValue.set(
                scrollProgress >= (prevProgRef.current ?? scrollProgress) ? 1 : 0
            )
            prevProgRef.current = scrollProgress
            return
        }

        // Determine scroll direction
        const previousScroll = prevProgRef.current
        prevProgRef.current = scrollProgress
        if (previousScroll === null) return // first update — only store value

        const isScrollingDown = scrollProgress > previousScroll + 0.001
        const targetProgress = isScrollingDown ? 1 : 0

        const currentAnimatedProgress = progressMotionValue.get()
        if (currentAnimatedProgress === targetProgress) return

        // Duration is proportional to remaining distance (constant speed)
        const remainingDistance = Math.abs(targetProgress - currentAnimatedProgress)
        const animationDuration = Math.max(0.001, remainingDistance * duration)

        animationRef.current?.stop?.()
        animationRef.current = animate(progressMotionValue, targetProgress, {
            duration: animationDuration,
            ease: 'linear'
        })
    })

    // Clean up on unmount
    useEffect(() => () => animationRef.current?.stop?.(), [])

    return (
        <Box
            ref={ref}
            whiteSpace="normal"
            wordBreak="normal"
            {...props}
            aria-live="off" // prevents screen readers from announcing color changes
        >
            {chars.map((char, index) => {
                if (char === ' ') return ' '
                const color = index < coloredCount ? to : from
                return (
                    <Box
                        as="span"
                        key={index}
                        color={color}
                        transition="color 0.2s linear"
                        display="inline"
                    >
                        {char}
                    </Box>
                )
            })}
        </Box>
    )
}

TypewrighterAnimation.propTypes = {
    from: PropTypes.string,
    to: PropTypes.string,
    duration: PropTypes.number,
    children: PropTypes.node,
    offset: PropTypes.array
}

export default TypewrighterAnimation
